import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as ACT from '../actions'
import { DealDetails } from './DealDetails'
import s from '../DealContainer.scss'
import { Container } from 'reactstrap'
import { toCategories } from '../serializers'
import { ScheduleTaskModal } from 'components/common/modals/ScheduleTaskModal'
import { toUsersSelect } from 'components/Users/serializers'
import { utcDate, getOrderedStatuses } from 'utils/helpers'
import { ScheduleTaskForAwardedCompanyModal } from './ScheduleTaskForAwardedCompanyModal'
import { makeGetRequest } from 'utils/api'
import { ContractorAwardedModal } from 'components/common/modals/ContractorAwardedModal'
import { LostOpportunityModal } from 'components/common/modals/LostOpportunityModal'
import cloneDeep from 'lodash/cloneDeep'
import { useParams, useNavigate } from 'react-router-dom'
import { useImmer } from 'use-immer'
import { toCompaniesWithTypes } from './serializers'

const initialState = {
  showScheduleTaskModal: false,
  showScheduleTaskForAwardedCompany: false,
  showContractorAwardedModal: false,
  showLostOpportunityModal: false,
  categories: [],
  sources: [],
  notBiddingReasons: [],
  reasons: [],
  isDealFormPristine: true,
  isWonByUs: false,
  newDealStatus: '',
  proposals: [],
  geographicalAreas: [],
  contractTypes: [],
  workTypes: []
}

const DealDetailsContainer = () => {
  const [state, setState] = useImmer(initialState)
  const { id: dealId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const deal = useSelector(state => state.deal)
  const users = useSelector(state => state.users)
  const { dealsStatuses, organizationStatuses } = useSelector(state => state.deals)

  const orderedStatuses = useMemo(
    () => getOrderedStatuses(dealsStatuses, organizationStatuses),
    [dealsStatuses, organizationStatuses]
  )

  const internalTeam = useMemo(() => {
    return deal.internalteam ? JSON.parse(deal.internalteam).team : []
  }, [deal.internalteam])

  useEffect(() => {
    dispatch({
      type: ACT.DEAL_FETCH_REQUESTED,
      payload: {
        id: dealId,
        navigate
      }
    })

    if (!dealsStatuses?.length) {
      dispatch({
        type: ACT.DEAL_STATUSES_FETCH_REQUESTED
      })
    }

    if (!organizationStatuses?.length) {
      dispatch({
        type: ACT.ORGANIZATION_STATUSES_FETCH_REQUESTED
      })
    }

    if (!users?.users?.length) {
      dispatch({
        type: ACT.USERS_FETCH_REQUESTED
      })
    }

    ;(async () => {
      const categories = await makeGetRequest('/v1/dealcategory')
      setState(draft => {
        draft.categories = toCategories(categories)
      })

      const { proposals } = await makeGetRequest(`/v1/deals/proposals-summary/${dealId}`)
      setState(draft => {
        draft.proposals = proposals
      })

      const sources = await makeGetRequest('/v1/dealsource')
      setState(draft => {
        draft.sources = toCategories(sources)
      })

      const notBiddingReasons = await makeGetRequest('/v1/deal-notbidding-reason')
      setState(draft => {
        draft.notBiddingReasons = toCategories(notBiddingReasons)
      })

      const reasons = await makeGetRequest('/v1/dealreason')
      setState(draft => {
        draft.reasons = toCategories(reasons)
      })

      const settings = await makeGetRequest('/v1/deals-settings')
      setState(draft => {
        draft.settings = settings
      })

      const geographicalAreas = await makeGetRequest('/v1/geographical-area')
      setState(draft => {
        draft.geographicalAreas = toCategories(geographicalAreas)
      })

      const contractTypes = await makeGetRequest('/v1/deal-contract-type')
      setState(draft => {
        draft.contractTypes = toCategories(contractTypes)
      })

      const scopes = await makeGetRequest('/v1/deal-scope')
      setState(draft => {
        draft.scopes = toCategories(scopes)
      })

      const workTypes = await makeGetRequest('/v1/deal-work-type')
      setState(draft => {
        draft.workTypes = toCategories(workTypes)
      })

      const internalBidStatuses = await makeGetRequest('/v1/deal-internal-bid-status')
      setState(draft => {
        draft.internalBidStatuses = toCategories(internalBidStatuses)
      })

      const bidStatuses = await makeGetRequest('/v1/deal-bid-status')
      setState(draft => {
        draft.bidStatuses = toCategories(bidStatuses)
      })

      const projectStatuses = await makeGetRequest('/v1/deal-project-status')
      setState(draft => {
        draft.projectStatuses = toCategories(projectStatuses)
      })

      const companiesOptions = await makeGetRequest('/v1/companies/deal-company-types')
      setState(draft => {
        draft.buildingOwners = toCompaniesWithTypes(companiesOptions.buildingOwners)
        draft.endUsers = toCompaniesWithTypes(companiesOptions.endUsers)
        draft.propertyManagers = toCompaniesWithTypes(companiesOptions.propertyManagers)
      })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateDeal = (deal, callback) => {
    dispatch({
      type: ACT.DEAL_UPDATE_REQUESTED,
      payload: {
        deal,
        fromDealDetails: true,
        callback
      }
    })
  }

  const updateDealProperty = (property, value) => {
    dispatch({
      type: ACT.DEAL_PROPERTY_UPDATE,
      payload: {
        property,
        value
      }
    })
  }

  const updateProposalsFollowUp = (id, followUpDate) => {
    dispatch({
      type: ACT.DEAL_UPDATE_PROPOSALS_FOLLOWUP_REQUESTED,
      payload: {
        id,
        followUpDate
      }
    })
  }

  const toggleScheduleTaskModal = () => {
    setState(draft => {
      draft.showScheduleTaskModal = !draft.showScheduleTaskModal
    })
  }

  const toggleContractorAwardedModal = params => {
    setState(draft => {
      draft.showContractorAwardedModal = !draft.showContractorAwardedModal
      draft.isWonByUs = params?.isWonByUs || false
      draft.newDealStatus = params?.newDealStatus ?? ''
    })
  }

  const toggleLostOpportunityModal = params => {
    setState(draft => {
      draft.showLostOpportunityModal = !draft.showLostOpportunityModal
      draft.newDealStatus = params?.newDealStatus ?? ''
    })
  }

  const updateDealStatusContractorAwarded = params => {
    const { awardedCompany, bidStatus, followUpDate, reason, isLostModal } = params

    const clonedDeal = cloneDeep(deal)
    const existingClientsIds = clonedDeal.dealCompanies.map(c => c.value)

    clonedDeal.awardedCompany = awardedCompany
    clonedDeal.bidStatus = bidStatus
    clonedDeal.status = state.newDealStatus
    if (followUpDate) clonedDeal.followUpDate = utcDate(followUpDate)
    if (reason) clonedDeal.noWinReason = reason

    updateDeal(clonedDeal)

    if (isLostModal) {
      toggleLostOpportunityModal()
    } else {
      toggleContractorAwardedModal()
    }

    if (awardedCompany && !existingClientsIds.includes(awardedCompany.value)) {
      toggleShowScheduleTaskForAwardedCompany()
    }
  }

  const showScheduleTaskModal = (params = {}) => {
    toggleScheduleTaskModal(null, null, params)
  }

  const createTask = task => {
    dispatch({
      type: ACT.CREATE_DEAL_TASK_REQUESTED,
      payload: {
        task,
        dealId
      }
    })
  }

  const toggleShowScheduleTaskForAwardedCompany = () => {
    setState(draft => {
      draft.showScheduleTaskForAwardedCompany = !draft.showScheduleTaskForAwardedCompany
    })
  }

  const addInternalTeamUser = (userRole, cb) => {
    dispatch({
      type: ACT.DEAL_ADD_INTERNAL_TEAM_REQUESTED,
      payload: {
        userRole,
        dealId
      }
    })

    cb && cb()
  }

  const removeInternalTeamUser = id => {
    dispatch({
      type: ACT.DEAL_REMOVE_INTERNAL_TEAM_REQUESTED,
      payload: {
        id,
        dealId
      }
    })
  }

  const setIsDealFormPristine = () => {
    setState(draft => {
      draft.isDealFormPristine = true
    })
  }

  const setIsDealFormDirty = () => {
    setState(draft => {
      draft.isDealFormPristine = false
    })
  }

  const deleteDeal = () => {
    dispatch({
      type: ACT.DEAL_DELETE_REQUESTED,
      payload: {
        id: dealId,
        navigate
      }
    })
  }

  return (
    <>
      <Container className={s.container}>
        <DealDetails
          toggleShowScheduleTaskForAwardedCompany={toggleShowScheduleTaskForAwardedCompany}
          users={toUsersSelect(users.users)}
          updateDeal={updateDeal}
          updateDealProperty={updateDealProperty}
          deal={deal}
          internalTeam={internalTeam}
          addInternalTeamUser={addInternalTeamUser}
          removeInternalTeamUser={removeInternalTeamUser}
          updateProposalsFollowUp={updateProposalsFollowUp}
          toggleContractorAwardedModal={toggleContractorAwardedModal}
          toggleLostOpportunityModal={toggleLostOpportunityModal}
          dealCategories={state.categories}
          dealWorkTypes={state.workTypes}
          dealBidStatuses={state.bidStatuses}
          internalBidStatuses={state.internalBidStatuses}
          dealProjectStatuses={state.projectStatuses}
          dealNotBiddingReasons={state.notBiddingReasons}
          dealReasons={state.reasons}
          dealSources={state.sources}
          dealScopes={state.scopes}
          settings={state.settings}
          isDealFormPristine={state.isDealFormPristine}
          setIsDealFormPristine={setIsDealFormPristine}
          setIsDealFormDirty={setIsDealFormDirty}
          deleteDeal={deleteDeal}
          dealsStatuses={dealsStatuses}
          orderedStatuses={orderedStatuses}
          proposals={state.proposals}
          geographicalAreas={state.geographicalAreas}
          dealContractTypes={state.contractTypes}
          buildingOwners={state.buildingOwners}
          endUsers={state.endUsers}
          propertyManagers={state.propertyManagers}
        />
      </Container>
      {state.showScheduleTaskModal && (
        <ScheduleTaskModal
          showScheduleTaskModal={state.showScheduleTaskModal}
          toggleScheduleTaskModal={toggleScheduleTaskModal}
          createTask={createTask}
          users={toUsersSelect(users.users)}
          deal={deal}
        />
      )}
      {state.showScheduleTaskForAwardedCompany && (
        <ScheduleTaskForAwardedCompanyModal
          toggleShowScheduleTaskForAwardedCompany={toggleShowScheduleTaskForAwardedCompany}
          showScheduleTaskForAwardedCompany={state.showScheduleTaskForAwardedCompany}
          showScheduleTaskModal={showScheduleTaskModal}
        />
      )}
      {state.showContractorAwardedModal && (
        <ContractorAwardedModal
          entity={deal}
          showContractorAwardedModal={state.showContractorAwardedModal}
          toggleContractorAwardedModal={toggleContractorAwardedModal}
          cancelCb={toggleContractorAwardedModal}
          onSave={updateDealStatusContractorAwarded}
          isWonByUs={state.isWonByUs}
        />
      )}
      {state.showLostOpportunityModal && (
        <LostOpportunityModal
          entity={deal}
          showLostOpportunityModal={state.showLostOpportunityModal}
          toggleLostOpportunityModal={toggleLostOpportunityModal}
          cancelCb={toggleLostOpportunityModal}
          onSave={updateDealStatusContractorAwarded}
          reasons={state.reasons}
          status={state.newDealStatus}
        />
      )}
    </>
  )
}

export default DealDetailsContainer
